import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Milestone, RefreshCw, Sparkles, Plus } from "lucide-react";

const CAT_COLOR = {
  engineering: "#1F75A8", customer: "#1D9E75", revenue: "#B8860B",
  product: "#7A3FB8", certification: "#0E7C7B", partnership: "#C0392B", growth: "#E0851B",
  release: "#5B21B6", pre_launch: "#64748B",
};

function relatedChips(related) {
  if (!related) return null;
  const items = [];
  ["baseline_ids", "certification_ids", "deployment_fingerprint_ids", "report_ids", "archive_ids"].forEach((k) => {
    (related[k] || []).forEach((v) => v && items.push(v));
  });
  return items.map((v) => (
    <span key={v} style={{ fontSize: 10, background: "#EEF2F7", padding: "2px 8px", borderRadius: 10, marginRight: 6 }}>{v}</span>
  ));
}

export default function TimelinePanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ title: "", category: "product", type: "", description: "" });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/timeline/milestones");
    setItems(data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const releases = items.filter((m) => m.category === "release");
  const events = items.filter((m) => m.category !== "release");

  const scan = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/timeline/scan", {});
      setMsg(`Institutional Memory scan: ${data.created_count} new milestone(s) recorded.`);
      await load();
    } catch (e) { setMsg("Scan failed."); }
    setBusy(false);
  };

  const createMilestone = async () => {
    if (!form.title || !form.type) { setMsg("Title and type required."); return; }
    setBusy(true); setMsg("");
    try {
      await api.post("/timeline/milestones", form);
      setForm({ title: "", category: "product", type: "", description: "" });
      setShowForm(false);
      await load();
    } catch (e) { setMsg("Create failed."); }
    setBusy(false);
  };

  return (
    <div data-testid="timeline-panel">
      <div className="toolbar">
        <div className="section-label" style={{ margin: 0 }}>
          <Milestone size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} /> Executive Timeline — Institutional Memory
        </div>
        <div className="spacer" />
        <button className="btn-ghost" onClick={() => setShowForm(!showForm)} data-testid="add-milestone-toggle"><Plus size={13} /> Add</button>
        <button className="btn-ghost" onClick={scan} disabled={busy} data-testid="scan-milestones-button"><Sparkles size={13} /> Auto-scan</button>
        <button className="btn-ghost" onClick={load}><RefreshCw size={13} /> Refresh</button>
      </div>

      {msg && <div className="card" data-testid="timeline-msg" style={{ borderColor: "#1F75A8", color: "#1F75A8" }}>{msg}</div>}

      {releases.length > 0 && (
        <div data-testid="version-history" style={{ marginBottom: 18 }}>
          <div className="section-label">System Version History</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {releases.map((m) => (
              <div key={m.milestone_id} className="card" data-testid={`release-${m.type}`}
                   style={{ flex: "1 1 220px", borderTop: `4px solid ${CAT_COLOR.release}` }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>{(m.kpis && m.kpis.version) || m.title}</span>
                  {m.kpis && m.kpis.status === "planned"
                    ? <span style={{ fontSize: 10, background: "#F3E8FF", color: "#5B21B6", padding: "2px 8px", borderRadius: 10 }}>PLANNED</span>
                    : <span style={{ fontSize: 10, background: "#E6F7F0", color: "#1D9E75", padding: "2px 8px", borderRadius: 10 }}>RELEASED</span>}
                </div>
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>{m.title}</div>
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>{m.description}</div>
                <div style={{ fontSize: 11, marginTop: 6, opacity: 0.55 }}>{new Date(m.date).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="card" data-testid="milestone-form" style={{ marginBottom: 12, display: "grid", gap: 8 }}>
          <input className="input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="milestone-title" />
          <input className="input" placeholder="Type (e.g. product_v1_1)" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="milestone-type" />
          <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {Object.keys(CAT_COLOR).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input className="input" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button className="btn-primary" onClick={createMilestone} disabled={busy} data-testid="save-milestone">Save milestone</button>
        </div>
      )}

      <div className="section-label">Milestones ({events.length})</div>
      {events.length === 0 && <div className="empty-state" data-testid="timeline-empty">No milestones yet. Run Auto-scan to generate from current state.</div>}
      <div style={{ display: "grid", gap: 10, position: "relative" }}>
        {events.map((m) => (
          <div key={m.milestone_id} className="card" data-testid={`milestone-${m.type}`}
               style={{ borderLeft: `4px solid ${CAT_COLOR[m.category] || "#888"}` }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700 }}>{m.title}</span>
              <span style={{ fontSize: 11, textTransform: "uppercase", color: CAT_COLOR[m.category] || "#888", letterSpacing: ".05em" }}>{m.category}</span>
              {m.auto_generated && <span style={{ fontSize: 10, opacity: 0.5 }}>auto</span>}
              <span className="spacer" />
              <span style={{ fontSize: 12, opacity: 0.6 }}>{new Date(m.date).toLocaleDateString()}</span>
            </div>
            {m.description && <div style={{ fontSize: 13, marginTop: 4, opacity: 0.85 }}>{m.description}</div>}
            <div style={{ marginTop: 6 }}>{relatedChips(m.related)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
